import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { PageShell } from "@project/user/components/page-shell";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  ArrowLeft,
  Clock,
  CalendarX,
  Coins,
  Stethoscope,
  HeartPulse,
} from "lucide-react";
import Marquee from "react-fast-marquee";
import { HealthcareDonationModal } from "@project/user/components/healthcare-donation-modal";

import healthcare1 from "@project/assets/Helthcare-1.webp";
import healthcare2 from "@project/assets/Healthcare-2.jpg";
import healthcare3 from "@project/assets/Healthcare-3.jpg";

import {
  hospitalProfileService,
  medicalStaffService,
} from "@project/services/admin";
import type { HospitalProfile } from "@project/types/hospital-profile";
import type { MedicalStaff } from "@project/types/medical-staff";


export default function HealthcareDetailPage() {
  const { id } = useParams({ from: "/_user/_care/healthcare/$id" as any });
  const [profiles, setProfiles] = useState<HospitalProfile[]>([]);
  const [staffList, setStaffList] = useState<MedicalStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [donationModalOpen, setDonationModalOpen] = useState(false);

  const bgImages = useMemo(() => [healthcare1, healthcare2, healthcare3], []);
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [bgImages.length]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [profilesRes, staffRes] = await Promise.all([
          hospitalProfileService.listPage({ page: 1, page_size: 100 }),
          medicalStaffService.listPage({ page: 1, page_size: 100 }),
        ]);
        setProfiles((profilesRes.data || []).filter(p => p.is_active));
        setStaffList(staffRes.data || []);
      } catch (err) {
        console.error("Failed to load healthcare detail page data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const activeProfile = useMemo(() => {
    return profiles.find(p => p.id === Number(id)) || null;
  }, [profiles, id]);

  const linkedDoctors = useMemo(() => {
    if (!activeProfile) {
      return [];
    }
    return staffList.filter(s => (activeProfile.doctors || []).includes(s.id));
  }, [activeProfile, staffList]);

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PageShell>
    );
  }

  if (!activeProfile) {
    return (
      <PageShell>
        <div className="mx-auto max-w-7xl px-4 py-16 text-center">
          <Stethoscope className="mx-auto h-12 w-12 animate-pulse text-muted-foreground" />
          <h2 className="mt-4 font-serif text-2xl font-extrabold text-foreground">
            Healthcare Unit Not Found
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The requested medical center could not be loaded or is currently
            inactive.
          </p>
          <div className="mt-6">
            <Link
              to="/healthcare"
              className="hover:bg-primary/90 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Healthcare Units
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="animate-fade-in mx-auto max-w-7xl space-y-12 px-4 pb-16 pt-8">
        {/* HEADER TITLE SECTION */}
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card p-6 md:p-10 shadow-xs">
          {/* Background image slideshow */}
          <div className="absolute inset-0 z-0">
            {bgImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt=""
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${idx === bgIndex ? "opacity-50" : "opacity-0"
                  }`}
              />
            ))}
            {/* Elegant dark overlay gradient to blend text and background */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/50 to-black/20" />
          </div>

          {/* Header Content */}
          <div className="relative z-10 flex flex-col items-center gap-6 text-center">
            {/* Back Button and Badge Row */}
            <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row sm:items-center">
              <Link
                to="/healthcare"
                className="border-white/20 bg-white/10 text-white shadow-2xs backdrop-blur-xs hover:border-white/40 group inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold transition-all duration-300 hover:-translate-x-0.5 hover:bg-white/20"
              >
                <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
                Back to Healthcare Units
              </Link>

              <Badge variant="secondary" className="bg-[#0052b4] text-white border-none shadow-sm px-3 py-0.5 text-xs font-bold">
                {activeProfile.highlights?.[0]?.title || "Care Services"}
              </Badge>

              {/* Spacer to balance the layout on desktop view */}
              <div className="hidden w-[180px] sm:block" aria-hidden="true" />
            </div>

            <div className="max-w-3xl space-y-3">
              <h1 className="font-serif text-3xl font-extrabold tracking-tight !text-white sm:text-4xl drop-shadow-md">
                {activeProfile.hospital_name}
              </h1>
              <p className="text-center text-sm font-medium leading-relaxed text-slate-200 drop-shadow-sm">
                {activeProfile.subtitle}
              </p>

              {/* Quick Info Row */}
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 pt-4 text-xs font-semibold">
                {activeProfile.hospital_timings && (
                  <div className="shadow-2xs flex items-center gap-2 rounded-xl  border-white/20 bg-black/30 px-3 py-1.5 text-white backdrop-blur-xs">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{activeProfile.hospital_timings}</span>
                  </div>
                )}
                {activeProfile.closed_on && (
                  <div className="shadow-2xs flex items-center gap-2 rounded-xl  border-amber-500/20 bg-amber-600/50 px-3 py-1.5 text-amber-200 backdrop-blur-xs">
                    <CalendarX className="h-3.5 w-3.5" />
                    <span>Closed on: {activeProfile.closed_on}</span>
                  </div>
                )}
                {(activeProfile.new_patient_fee ||
                  activeProfile.existing_patient_fee) && (
                    <div className="shadow-2xs flex items-center gap-2 rounded-xl  border-emerald-500/20 bg-emerald-600/50 px-3 py-1.5 text-emerald-200 backdrop-blur-xs">
                      <Coins className="h-3.5 w-3.5" />
                      <span>
                        {activeProfile.new_patient_fee
                          ? `New Patient: ₹${parseInt(String(activeProfile.new_patient_fee))}`
                          : ""}
                        {activeProfile.new_patient_fee &&
                          activeProfile.existing_patient_fee
                          ? " | "
                          : ""}
                        {activeProfile.existing_patient_fee
                          ? `Existing: ₹${parseInt(String(activeProfile.existing_patient_fee))}`
                          : ""}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Layout Block: Two Columns Grid */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Panel Column (Left 2/3 width) */}
          <div className="space-y-8 lg:col-span-2">
            {/* Section 2: Treatments & Medical Services */}
            <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
              <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                <h3 className="font-serif text-lg font-extrabold text-foreground">
                  Treatments &amp; Medical Services
                </h3>
              </div>
              {activeProfile.treatments &&
                activeProfile.treatments.length > 0 ? (
                <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                  {activeProfile.treatments.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-xs font-semibold text-foreground"
                    >
                      <span className="font-bold text-[#0052b4]">•</span>
                      <span className="leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  No specific treatments listed.
                </p>
              )}
            </div>

            {/* Section 3: Advanced Equipment & Infrastructure */}
            {activeProfile.equipments &&
              activeProfile.equipments.length > 0 && (
                <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                  <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                    <h3 className="font-serif text-lg font-extrabold text-foreground">
                      Advanced Equipment &amp; Infrastructure
                    </h3>
                  </div>
                  <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                    {activeProfile.equipments.map((eq, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-xs font-semibold leading-snug text-foreground"
                      >
                        <span className="shrink-0 font-bold text-[#0052b4]">
                          •
                        </span>
                        <span>{eq}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Section 1: Attending Doctors & Specialists */}
            <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
              <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                <h3 className="font-serif text-lg font-extrabold text-foreground">
                  Attending Doctors &amp; Specialists
                </h3>
              </div>
              {linkedDoctors.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {linkedDoctors.map(doc => (
                    <div
                      key={doc.id}
                      className="border-border/80 shadow-2xs flex flex-col justify-between space-y-3 rounded-2xl border bg-background p-5 transition-colors hover:border-[#0052b4]/40"
                    >
                      <div className="space-y-2">
                        <div>
                          <h4 className="font-serif text-base font-bold text-foreground">
                            {doc.name}
                          </h4>
                          {doc.qualification && (
                            <Badge
                              variant="secondary"
                              className="mt-1 bg-[#0052b4]/10 text-[10px] font-semibold text-[#0052b4]"
                            >
                              {doc.qualification}
                            </Badge>
                          )}
                        </div>
                        {doc.designation && (
                          <p className="text-xs font-medium text-muted-foreground">
                            {doc.designation}
                          </p>
                        )}
                      </div>
                      <div className="border-border/50 rounded-xl border-t bg-amber-500/10 p-2.5 pt-3 text-[11px] font-semibold text-amber-800">
                        <span>
                          Timings:{" "}
                          {doc.timings || activeProfile.hospital_timings}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  No doctors assigned to this facility.
                </p>
              )}
            </div>
          </div>

          {/* Sidebar Column (Right 1/3 width) */}
          <div className="space-y-6">

            {/* Sidebar Donation Card: moved here from header */}
            <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
              <div className="border-l-3 border-rose-600 py-0.5 pl-3">
                <h3 className="font-serif text-base font-extrabold text-foreground">
                  Support &amp; Donate
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Support this healthcare unit with a donation to help maintain
                services and equipment.
              </p>
              <div className="pt-2">
                <Button
                  onClick={() => setDonationModalOpen(true)}
                  className="shadow-xs w-full flex items-center justify-center gap-2 rounded-xl bg-[#0052b4] px-4 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-[#0052b4]/90"
                >
                  <HeartPulse className="h-4 w-4" /> Donate
                </Button>
              </div>
            </div>

            {/* Sidebar Section 1: Facilities Provided Checklist */}
            <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
              <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                <h3 className="font-serif text-base font-extrabold text-foreground">
                  Facilities Provided
                </h3>
              </div>
              {activeProfile.facilities &&
                activeProfile.facilities.length > 0 ? (
                <div className="space-y-3">
                  {activeProfile.facilities.map((fac, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2.5 text-xs font-semibold text-foreground"
                    >
                      <span className="font-bold text-[#0052b4]">•</span>
                      <span>{fac}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs italic text-muted-foreground">
                  No facilities listed.
                </p>
              )}
            </div>


            {/* Sidebar Section 2: Nursing & Care Staff */}
            {activeProfile.nurses && activeProfile.nurses.length > 0 && (
              <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                <div className="border-l-3 border-emerald-600 py-0.5 pl-3">
                  <h3 className="font-serif text-base font-extrabold text-foreground">
                    Nursing &amp; Care Staff
                  </h3>
                </div>
                <div className="space-y-3.5">
                  {activeProfile.nurses.map((staff, idx) => (
                    <div
                      key={idx}
                      className="border-border/40 flex flex-col border-b pb-2 last:border-0 last:pb-0"
                    >
                      <span className="text-xs font-bold text-foreground">
                        {staff.name}
                      </span>
                      <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">
                        {staff.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sidebar Section 3: Emergency Call Box */}
            {activeProfile.emergency_contact_name && (
              <div className="shadow-xs space-y-4 rounded-3xl border border-amber-500/30 p-6">
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-900">
                    Emergency Medical Contact
                  </p>
                  <p className="mt-1.5 text-sm font-extrabold leading-snug text-foreground">
                    {activeProfile.emergency_contact_name}
                  </p>
                </div>
                {activeProfile.emergency_contact_number && (
                  <a
                    href={`tel:${activeProfile.emergency_contact_number}`}
                    className="shadow-2xs block rounded-xl bg-amber-500/20 py-3 text-center text-xs font-black text-amber-800 transition-colors hover:bg-amber-500/25 hover:underline"
                  >
                    📞 Call: {activeProfile.emergency_contact_number}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* HIGHLIGHT CARDS STRIP */}
        {activeProfile.highlights && activeProfile.highlights.length > 0 && (
          <div className="space-y-4">
            <div className="text-center text-base font-extrabold uppercase tracking-wider text-amber-700">
              Key Highlights &amp; Accomplishments
            </div>
            <Marquee pauseOnHover speed={30} gradient={false} className="py-2">
              {activeProfile.highlights.map((hl, idx) => (
                <div
                  key={idx}
                  className="shadow-xs mx-3 w-[290px] flex-shrink-0 space-y-1.5 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#0052b4]/40 hover:shadow-md"
                >
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#0052b4]">
                    {hl.title || "KEY HIGHLIGHT"}
                  </p>
                  <p className="text-xs font-bold leading-snug text-foreground">
                    {hl.description}
                  </p>
                </div>
              ))}
            </Marquee>
          </div>
        )}
      </div>

      <HealthcareDonationModal
        open={donationModalOpen}
        onOpenChange={setDonationModalOpen}
        hospitalName={activeProfile.hospital_name}
        hospitalId={activeProfile.id}
      />
    </PageShell>
  );

}
